import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import StatusBadge from "@/components/app/StatusBadge";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import PunchButton from "@/components/app/PunchButton";
import { Clock, MapPin, Users, CalendarDays, Printer } from "lucide-react";

function groupByDate(list) {
  const g = {};
  list.forEach((j) => {
    g[j.scheduled_date] = g[j.scheduled_date] || [];
    g[j.scheduled_date].push(j);
  });
  return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
}

export default function MyJobs() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [tab, setTab] = useState("upcoming");

  const load = () => api.get("/jobs", { params: { mine: true } }).then((r) => setJobs(r.data));
  useEffect(() => { load(); }, []);
  useAutoRefresh(load, 15000);

  const todayStr = new Date().toISOString().slice(0, 10);
  const upcoming = jobs.filter((j) => j.scheduled_date >= todayStr && j.status !== "annullato" && j.status !== "completato");
  const past = jobs.filter((j) => j.scheduled_date < todayStr || j.status === "completato");
  const grouped = useMemo(() => groupByDate(tab === "upcoming" ? upcoming : past), [tab, jobs]);

  return (
    <div className="space-y-8">
      <header>
        <div className="text-xs uppercase tracking-widest text-sky-600 font-semibold mb-2">Ciao, {user?.name?.split(" ")[0]}</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">I Miei Traslochi</h1>
        <p className="text-slate-500 mt-2 text-sm">Qui trovi tutti i turni che ti sono stati assegnati.</p>
      </header>

      <PunchButton />

      <div className="inline-flex bg-slate-100 rounded-xl p-1" data-testid="my-jobs-tabs">
        <button
          onClick={() => setTab("upcoming")}
          data-testid="tab-upcoming"
          className={`px-5 py-2 rounded-lg text-sm font-semibold transition ${tab === "upcoming" ? "bg-white shadow text-slate-900" : "text-slate-600"}`}
        >
          Prossimi ({upcoming.length})
        </button>
        <button
          onClick={() => setTab("past")}
          data-testid="tab-past"
          className={`px-5 py-2 rounded-lg text-sm font-semibold transition ${tab === "past" ? "bg-white shadow text-slate-900" : "text-slate-600"}`}
        >
          Storico ({past.length})
        </button>
      </div>

      {grouped.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-16 text-center">
          <CalendarDays className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <div className="text-slate-500 font-semibold">
            {tab === "upcoming" ? "Nessun trasloco in programma." : "Nessun trasloco nello storico."}
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          {grouped.map(([date, list]) => (
            <div key={date}>
              <div className="flex items-center gap-3 mb-3">
                <div className="font-mono text-xs uppercase tracking-widest font-bold text-slate-400">{date}</div>
                <div className="h-px flex-1 bg-slate-200" />
                <div className="text-xs text-slate-500 font-semibold">{list.length} {list.length === 1 ? "lavoro" : "lavori"}</div>
              </div>
              <div className="grid gap-4 lg:grid-cols-2">
                {list.map((j) => (
                  <div
                    key={j.id}
                    className="bg-white rounded-2xl border border-slate-200 p-5 hover:border-sky-400 hover:shadow-md transition group"
                    data-testid={`my-job-${j.id}`}
                  >
                    <Link to={`/lavori/${j.id}`} className="block">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-display font-bold text-lg text-slate-900 truncate">{j.client_name}</div>
                          <div className="font-mono text-xs text-slate-400 flex items-center gap-1 mt-1">
                            <Clock className="w-3 h-3" />{j.start_time}{j.end_time && ` – ${j.end_time}`}
                            {j.depot_time && (
                              <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200 rounded px-1.5 py-0.5">
                                🏭 {j.depot_time}
                              </span>
                            )}
                          </div>
                        </div>
                        <StatusBadge status={j.status} />
                      </div>
                      <div className="mt-4 space-y-1.5 text-sm text-slate-600">
                        <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-sky-500 shrink-0 mt-0.5" /><span className="truncate">{j.address_from}</span></div>
                        <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /><span className="truncate">{j.address_to}</span></div>
                      </div>
                    </Link>
                    <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-xs text-slate-500 truncate">
                        <Users className="w-3.5 h-3.5" />
                        {(j.assigned_employees || []).map((e) => e.name).join(", ")}
                      </div>
                      <Link to={`/stampa/${j.id}`} target="_blank" className="text-slate-400 hover:text-sky-600" data-testid={`my-job-print-${j.id}`}>
                        <Printer className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
